import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signInWithCustomToken,
  sendEmailVerification,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import toast from 'react-hot-toast';
import { createProtectedAccount } from '../services/createAccountService';
import {
  hasManagementRoleFromRoles,
  isCommercialAgentOnlyProfile,
  isCommercialAgentProfile
} from '../utils/roleAccess';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Charger le profil utilisateur depuis Firestore
  const loadUserProfile = async (uid) => {
    if (!uid) return null;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profileData = { id: userDoc.id, ...userDoc.data() };
        
        // Migration : convertir l'ancien format (role unique) vers le nouveau format (roles array)
        if (profileData.role && !profileData.roles) {
          // Si on a un ancien rôle unique, le convertir en tableau
          profileData.roles = [profileData.role];
          // Garder aussi l'ancien champ pour compatibilité
        } else if (!profileData.roles) {
          // Si aucun rôle n'est défini, utiliser 'user' par défaut
          profileData.roles = ['user'];
        }
        
        // S'assurer que roles est toujours un tableau
        if (!Array.isArray(profileData.roles)) {
          profileData.roles = profileData.roles ? [profileData.roles] : ['user'];
        }
        
        setUserProfile(profileData);
        return profileData;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      return null;
    }
  };

  // Inscription
  const signup = async (email, password, additionalData = {}) => {
    try {
      const displayName = String(
        additionalData.displayName || additionalData.fullName || additionalData.name || ''
      ).trim();
      const result = await createProtectedAccount({
        email,
        password,
        name: displayName,
        phone: additionalData.phone || '',
        website: additionalData.website || '',
        magicLinkToken: additionalData.magicLinkToken || '',
        acceptedTerms: additionalData.acceptedTerms === true,
        formStartedAt: additionalData.formStartedAt,
        lang: additionalData.lang || 'fr'
      });
      const userCredential = result.customToken
        ? await signInWithCustomToken(auth, result.customToken)
        : await signInWithEmailAndPassword(auth, email, password);
      if (userCredential.user && !userCredential.user.emailVerified) {
        try {
          await sendEmailVerification(userCredential.user);
        } catch (verifyErr) {
          console.warn('[signup] email verification skipped', verifyErr?.message || verifyErr);
        }
      }
      await loadUserProfile(userCredential.user.uid);
      return userCredential.user;
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      throw error;
    }
  };

  // Connexion
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Charger le profil
      await loadUserProfile(user.uid);
      
      return user;
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      throw error;
    }
  };

  // Déconnexion
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      toast.success('Déconnexion réussie');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      toast.error('Erreur lors de la déconnexion');
    }
  };

  // Obtenir les rôles de l'utilisateur (tableau)
  const getRoles = () => {
    if (!userProfile) return [];
    
    // Support des deux formats : nouveau (roles array) et ancien (role unique)
    if (userProfile.roles && Array.isArray(userProfile.roles)) {
      return userProfile.roles;
    }
    if (userProfile.role) {
      return [userProfile.role];
    }
    return ['user'];
  };

  // Vérifier si l'utilisateur a un rôle spécifique
  const hasRole = (role) => {
    const roles = getRoles();
    return roles.includes(role);
  };

  // Vérifier si l'utilisateur est admin
  const isAdmin = () => {
    return hasRole('admin');
  };

  // Vérifier si l'utilisateur est chauffeur
  const isDriver = () => {
    return hasRole('driver') || hasRole('chauffeur');
  };

  // Vérifier si l'utilisateur est chef d'entreprise
  const isBusinessOwner = () => {
    return hasRole('business') || hasRole('businessOwner') || hasRole('owner');
  };

  // Vérifier si l'utilisateur est loueur
  const isRentalOwner = () => {
    return hasRole('rental') || hasRole('rental_owner') || hasRole('loueur');
  };

  // Agent de point cash (dépôt / retrait espèces)
  const isCashAgent = () => {
    return hasRole('cash_agent');
  };

  /** Agent commercial (rôle actif ou dossier en cours / traité). */
  const isCommercialAgent = () => isCommercialAgentProfile(userProfile);

  /** Admin, chauffeur, commerce, location, cash agent. */
  const hasManagementRole = () => hasManagementRoleFromRoles(getRoles());

  /** Compte centré agent commercial (pas d’autre espace de gestion). */
  const isCommercialAgentOnly = () => isCommercialAgentOnlyProfile(userProfile);

  // Vérifier si l'utilisateur a au moins un des rôles spécifiés
  const hasAnyRole = (requiredRoles) => {
    const userRoles = getRoles();
    return requiredRoles.some(role => userRoles.includes(role));
  };

  // Écouter les changements d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    login,
    signup,
    logout,
    loadUserProfile,
    getRoles,
    hasRole,
    isAdmin,
    isDriver,
    isBusinessOwner,
    isRentalOwner,
    isCashAgent,
    isCommercialAgent,
    hasManagementRole,
    isCommercialAgentOnly,
    hasAnyRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

